import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { startAuthentication } from '@simplewebauthn/browser'
import { FaKey } from 'react-icons/fa'
import { Box, Button, FormControl, Icon, Input, SimpleModal, Stack, Text, Alert } from '@/components/lib'
import api from '@/api'

// "Confirm it's you": the mid-session re-authentication the API asks for
// before an action that must not act on a session alone — signing data,
// and in time locking a database or changing permissions. This is the one
// place in the web app that asks; anything needing a proof of presence
// uses it rather than building its own dialog.
//
//   const { withReauth } = useReauthentication()
//   withReauth('Sign this response', token =>
//     api.trials.signResponses(trial._id, data, token, onDone, onFail))
//
// `withReauth` first uses a proof it already holds (one dialog covers every
// request made in the next few minutes — signing a casebook is many
// requests), otherwise opens the dialog, then calls the function with the
// token to send as the X-Reauth-Token header. If the API still answers
// with `details.code === 'reauth'` (the proof expired in flight), it asks
// once more and retries. Cancelling the dialog rejects with `cancelled`.
//
// What the dialog asks for comes from the API: a passkey and/or an
// authenticator code, whichever the account has, and the password as well
// when the login session is more than a day old.

const ReauthenticationContext = createContext(null)

export function ReauthenticationProvider ({ children }) {
  const [request, setRequest] = useState(null) // { purpose, resolve, reject }
  const proof = useRef(null) // { token, expiresAt }

  const confirm = useCallback(purpose => new Promise((resolve, reject) => {
    const held = proof.current
    if (held && new Date(held.expiresAt).getTime() - Date.now() > 15000) return resolve(held.token)
    setRequest({ purpose, resolve, reject })
  }), [])

  const withReauth = useCallback(async (purpose, run) => {
    const attempt = async (retrying) => {
      const token = await confirm(purpose)
      return new Promise((resolve, reject) => {
        run(token, resolve, async err => {
          if (err?.details?.code === 'reauth' && !retrying) {
            proof.current = null
            try { resolve(await attempt(true)) } catch (e) { reject(e) }
          } else reject(err)
        })
      })
    }
    return attempt(false)
  }, [confirm])

  const done = result => {
    proof.current = { token: result.reauthToken, expiresAt: result.expiresAt }
    request?.resolve(result.reauthToken)
    setRequest(null)
  }
  const cancel = () => {
    request?.reject(Object.assign(new Error('Cancelled'), { cancelled: true }))
    setRequest(null)
  }

  return (
    <ReauthenticationContext.Provider value={{ confirm, withReauth, forget: () => { proof.current = null } }}>
      {children}
      {request && <ReauthenticateDialog purpose={request.purpose} onDone={done} onCancel={cancel} />}
    </ReauthenticationContext.Provider>
  )
}

export default function useReauthentication () {
  const context = useContext(ReauthenticationContext)
  if (!context) throw new Error('useReauthentication needs a ReauthenticationProvider above it')
  return context
}

function ReauthenticateDialog ({ purpose, onDone, onCancel }) {
  const [options, setOptions] = useState(null)
  const [error, setError] = useState(null)
  const [otp, setOtp] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api.accounts.reauthenticationOptions(setOptions, err => setError(err?.message || 'Unable to start'))
  }, [])

  function submit (data) {
    setBusy(true)
    setError(null)
    api.accounts.reauthenticate({ ...data, password: options?.passwordRequired ? password : undefined }, result => {
      setBusy(false)
      onDone(result)
    }, err => {
      setBusy(false)
      setError(err?.message || 'That wasn\'t recognised')
    })
  }

  async function usePasskey () {
    try {
      const assertion = await startAuthentication({ optionsJSON: options.passkeyOptions })
      submit({ passkeyAssertion: assertion })
    } catch (err) {
      if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') setError(err?.message || 'Your passkey wasn\'t recognised')
    }
  }

  const passwordOk = !options?.passwordRequired || password.length > 0
  const nothingToOffer = options && !options.canReauthenticate

  return (
    <SimpleModal
      isOpen
      onClose={onCancel}
      closeOnInteractOutside={false}
      header='Confirm it&apos;s you'
      body={
        <Stack gap={4}>
          <Text>
            <strong>{purpose}</strong> needs you to confirm it&apos;s you, so the record shows a deliberate act by you and nobody else.
          </Text>
          {error && <Alert status='error' variant='left-accent'>{error}</Alert>}
          {!options && !error && <Text color='fg.muted'>One moment…</Text>}
          {nothingToOffer &&
            <Alert status='warning' variant='left-accent'>
              Your account has no passkey or authenticator app to confirm with. Add one from <strong>Settings → Password &amp; two-factor</strong>, then try again.
            </Alert>}
          {options?.canReauthenticate &&
            <form onSubmit={e => { e.preventDefault(); if (otp) submit({ otp }) }}>
              <Stack gap={4}>
                {options.passwordRequired &&
                  <FormControl label='Your password' helperText='You signed in more than a day ago, so your password is needed as well.'>
                    <Input type='password' autoComplete='current-password' value={password} onChange={e => setPassword(e.target.value)} autoFocus />
                  </FormControl>}
                {options.passkey &&
                  <FormControl label='Use your passkey'>
                    <Button type='button' disabled={busy || !passwordOk} onClick={usePasskey}><Icon as={FaKey} mr={2} /> Confirm with a passkey</Button>
                  </FormControl>}
                {options.otp &&
                  <FormControl label={options.passkey ? 'Or enter a one-time code' : 'Enter a one-time code'} helperText='The six-digit code from your authenticator app.'>
                    <Stack direction='row'>
                      <Input type='number' inputMode='numeric' autoComplete='one-time-code' placeholder='123456' value={otp} onChange={e => setOtp(e.target.value)} autoFocus={!options.passwordRequired} />
                      <Button type='submit' colorPalette='primary' disabled={busy || !passwordOk || otp.length < 6} loading={busy}>Confirm</Button>
                    </Stack>
                  </FormControl>}
              </Stack>
            </form>}
        </Stack>
      }
      footer={<Box><Button variant='ghost' onClick={onCancel}>Cancel</Button></Box>}
    />
  )
}
